import {
  useAddCollection,
  useCollectionList,
  useProductAddFavorites,
  useProductDeleteFavorites,
} from "@apis/collection";
import { VDBText } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import CollectionModal from "@components/molecules/Collections/CollectionModal";
import LoaderModel from "@components/molecules/LoaderModel/LoaderModel";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "@scenes/favorites/utils/collectionSchema";
import { FavoritesEnum } from "@scenes/favorites/utils/FavoritesUtils";
import { getToastBottomMargin } from "@scenes/searchResults/components/utils";
import { Diamond } from "@src/types/DiamondDetail";
import { ProductCategory } from "@utils/constants";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Pressable, Platform } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useQueryClient } from "@tanstack/react-query";
import styled, { useTheme } from "styled-components/native";
import FavoritesModel from "./FavoritesModel";
import { useTranslation } from "react-i18next";
import { VDBIcons } from "@utils/VDBIcons";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { useAnimation } from "../../../../contexts/AnimationContext";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import { Portal } from "@gorhom/portal";
import { GALLERY_COLLECTION_OVERLAY_HOST } from "@components/molecules/GalleryViewModel/constants";

interface FavoriteModalProps {
  itemDetail?: Diamond;
  productType?: string;
  scrollValue?: any;
  /**
   * When this button lives inside the fullscreen GalleryViewModal (an RN Modal),
   * the collection picker must not open as a second nested RN Modal or it
   * collapses when the create-form keyboard appears. Instead it is teleported
   * (via Portal) into a host inside GalleryViewModal and rendered as an
   * in-window overlay.
   */
  renderCollectionInWindow?: boolean;
}

const FavoriteButton: React.FC<FavoriteModalProps> = ({
  itemDetail,
  productType,
  scrollValue,
  renderCollectionInWindow = false,
}) => {
  const theme = useTheme();

  const [showLoaderModal, setLoaderModal] = useState(false);
  const { triggerAnimation } = useAnimation();
  const showFavoritesAnimation = useSelector(
    (state: any) =>
      state.globalData?.app_settings?.showFavoritesAnimation ?? false
  );

  let enumProductType = productType as ProductCategory;

  const [showCollectionListModal, setShowCollectionListModal] = useState(false);

  const addToFavorites = useProductAddFavorites(enumProductType);
  const removeFromFavorites = useProductDeleteFavorites();
  const queryClient = useQueryClient();
  const [itemIdArray, setItemIdArray] = useState<number[]>([]);
  const [mItem, setmItem] = useState<Diamond | undefined>(undefined);
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.userInfo);
  const isGuest = userInfo.isGuest;
  const dispatch = useDispatch();

  const [enableCollectionButton, setEnableCollectionButton] = useState(false);
  const [isCollectionEventLoading, setCollectionEventLoading] = useState(false);

  const { bottom } = useSafeAreaInsets();

  const addCollection = useAddCollection();
  const { jcSelectedOption, personalization } = useJewelryConfiguratorState();

  const { control, handleSubmit, setValue } = useForm({
    resolver: yupResolver(schema),
  });

  const favoriteItemIdData = () => {
    queryClient.refetchQueries({ queryKey: ["SEARCH_RESULTS"] });
  };

  const [isCreateNewCollectionModalOpen, setIsCreateNewCollectionModalOpen] =
    useState(false);
  const [collectionCount, setCollectionCount] = useState(0);

  const collectionListCall = useCollectionList(1, 99, "mix_sort");
  useEffect(() => {
    const collections = collectionListCall.data?.data.collections;
    if (collections) {
      setCollectionCount(collections.length);
    } else {
      setCollectionCount(0);
    }
  }, [collectionListCall.data?.data.collections]);

  const addToFavoritesData = (isFavorite: boolean) => {
    setLoaderModal(true);
    if (isFavorite === undefined) {
      isFavorite = false;
    }

    let _payload = {
      type: "mix",
      ...([ProductCategory.LAB_GROWN_DIAMOND, ProductCategory.DIAMOND].includes(
        enumProductType
      ) && {
        lab_grown: productType === ProductCategory.LAB_GROWN_DIAMOND,
        diamond_id: itemDetail?.id ?? 0,
      }),
      ...(productType === ProductCategory.GEMSTONE && {
        gemstone_id: itemDetail?.id ?? 0,
      }),
      ...(productType === ProductCategory.JEWELRY && {
        stock_item_id: itemDetail?.id ?? 0,
      }),
    };

    if (productType === ProductCategory.JEWELRY) {
      _payload = {
        ..._payload,
        ...(Object.keys(jcSelectedOption.current.option_value).length > 0 && {
          config: jcSelectedOption.current.option_value,
        }),
      };
      if (personalization) {
        _payload = {
          ..._payload,
          ...(Object.keys(personalization).length > 0 && {
            personalization: personalization ?? {},
          }),
        };
      }
    }
    if (!isFavorite) {
      addToFavorites.mutate(
        {
          favorite: _payload,
        },
        {
          onSuccess(data) {
            const diamondTempFavArray = new Array<number>(0);
            diamondTempFavArray.push(data.data.favorite.diamond_id);
            itemDetail.is_favorite = true;
            showToastMessage(itemDetail, true);
            setLoaderModal(false);
            favoriteItemIdData();
          },
          onSettled() {
            //setShowApiLoader(false);
            setLoaderModal(false);
          },
        }
      );
    } else {
      removeFromFavorites.mutate(
        {
          id: itemDetail?.id ?? 0,
        },
        {
          onSuccess: (data: any) => {
            const diamondTempFavArray = new Array<number>(0);
            diamondTempFavArray.push(data.data.favorite.diamond_id);
            itemDetail.is_favorite = false;
            showToastMessage(itemDetail, false);
            setLoaderModal(false);
            favoriteItemIdData();
          },
        }
      );
    }
  };

  const showToastMessage = (item?: Diamond, isFavorite?: boolean) => {
    showToast(
      isFavorite
        ? `${item?.short_title} ${t("searchResult.addToFavorites.text")}`
        : `${item?.short_title} ${t("searchResult.removeFromFavorites.text")}`,

      isFavorite ? (
        <Pressable onPress={() => showCollectionModal(item?.id ?? 0, item)}>
          <VDBText color={theme["link-static"]}>
            {t("searchResult.tapToChangeCollection.text")}
          </VDBText>
        </Pressable>
      ) : (
        ""
      ),
      ToastTypes.SUCCESS,
      true,
      true,
      // Android/web honor bottomMargin directly; nudge it up a little inside
      // the gallery. Other usages (search results, etc.) keep the default.
      getToastBottomMargin(scrollValue, bottom) +
        (renderCollectionInWindow ? 24 : 0),
      undefined,
      true,
      4000,
      false,
      0,
      undefined,
      undefined,
      undefined,
      renderCollectionInWindow ? 80 : undefined
    );
  };

  const showCollectionModal = (id: number, item: Diamond) => {
    setmItem(item);
    setItemIdArray([id]);
    if (collectionCount > 0) {
      setShowCollectionListModal(true);
    } else {
      setIsCreateNewCollectionModalOpen(true);
    }
  };

  const checkEnableCollectionButton = (etValue: string) => {
    setEnableCollectionButton(!!etValue);
  };

  const onSubmit = (mData: any) => {
    const alteredCollectionName = mData.collectionName.trim();

    addCollection.mutate(
      {
        name: alteredCollectionName,
        type: 5,
      },
      {
        onSuccess() {
          setCollectionEventLoading(false);
          setIsCreateNewCollectionModalOpen(false);
          setValue(FavoritesEnum.collection_name, "");
          checkEnableCollectionButton("");
          setTimeout(() => {
            showToast(
              t("global.text.done"),
              t("favorites.newCollectionCreated.text"),
              ToastTypes.SUCCESS,
              true,
              true,
              getToastBottomMargin(scrollValue, bottom) +
                (renderCollectionInWindow ? 24 : 0),
              undefined,
              true,
              4000,
              false,
              0,
              undefined,
              undefined,
              undefined,
              renderCollectionInWindow ? 80 : undefined
            );
          }, 1000);
          collectionListCall.refetch().then();
        },
      }
    );
  };

  const favoriteRemoved = (itemId: Array<number>) => {
    if (itemId && itemId.length > 0 && itemDetail?.id === itemId[0]) {
      itemDetail.is_favorite = false;
      favoriteItemIdData();
    }
  };

  return (
    <>
      <Pressable
        onPress={(event) => {
          if (isGuest) {
            dispatch(setLoginModalType("default"));
            return;
          }
          if (
            !itemDetail?.is_favorite &&
            Platform.OS === "web" &&
            theme.isLargeScreen &&
            showFavoritesAnimation &&
            event.currentTarget
          ) {
            const rect = (event.currentTarget as any).getBoundingClientRect();
            const heartPosition = {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2,
            };

            triggerAnimation(heartPosition);
          }

          addToFavoritesData(itemDetail?.is_favorite);
        }}
      >
        <IconContainer>
          <VDBIcon
            name={itemDetail?.is_favorite ? VDBIcons.FAV : VDBIcons.FAV_EMPTY}
            size={24}
            color={theme["error-01"]}
            variant={IconsVariant.VDBCustom}
          />
        </IconContainer>
      </Pressable>

      <LoaderModel
        isOpen={showLoaderModal}
        onClose={() => setLoaderModal(false)}
      />

      {renderCollectionInWindow ? (
        <Portal hostName={GALLERY_COLLECTION_OVERLAY_HOST}>
          <CollectionModal
            isOpen={showCollectionListModal}
            onClose={() => setShowCollectionListModal(false)}
            itemIdArray={itemIdArray}
            favItemIdArray={itemIdArray ?? []}
            item={mItem}
            collectionItemIdArray={[]}
            favoriteRemoved={favoriteRemoved}
            enableToastMessage={true}
            inWindow={true}
          />
        </Portal>
      ) : (
        <CollectionModal
          isOpen={showCollectionListModal}
          onClose={() => setShowCollectionListModal(false)}
          itemIdArray={itemIdArray}
          favItemIdArray={itemIdArray ?? []}
          item={mItem}
          collectionItemIdArray={[]}
          favoriteRemoved={favoriteRemoved}
          enableToastMessage={true}
        />
      )}

      <FavoritesModel
        control={control}
        setValue={setValue}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        isCollectionEventLoading={isCollectionEventLoading}
        setIsCreateNewCollectionModalOpen={setIsCreateNewCollectionModalOpen}
        isCreateNewCollectionModalOpen={isCreateNewCollectionModalOpen}
        checkEnableCollectionButton={checkEnableCollectionButton}
        setCollectionEventLoading={setCollectionEventLoading}
        enableCollectionButton={enableCollectionButton}
      />
    </>
  );
};

const IconContainer = styled.View<any>`
  padding: ${SpaceType.Basic}px;
`;

export default FavoriteButton;
